import { mockClients } from "../../../mocks/clients";
import { mockAppointments } from "../../../mocks/appointments";

const today = "2026-03-24";
const tomorrow = "2026-03-25";

const todayApps = mockAppointments.filter((a) => a.date === today && a.status !== "Tamamlandı");
const tomorrowApps = mockAppointments.filter((a) => a.date === tomorrow);
const delayed = mockClients.filter((c) => c.status === "Gecikti");
const pending = mockClients.filter((c) => c.status !== "Tamamlandı" && c.status !== "Gecikti");
const completed = mockClients.filter((c) => c.status === "Tamamlandı");

const statCards = [
  {
    label: "Toplam Müşteri",
    value: mockClients.length,
    icon: "ri-user-star-line",
    color: "#a855f7",
    bg: "rgba(168,85,247,0.2)",
    border: "rgba(168,85,247,0.35)",
    change: "+3 bu hafta",
  },
  {
    label: "Bugünkü İşler",
    value: todayApps.length,
    icon: "ri-sun-line",
    color: "#d4af37",
    bg: "rgba(212,175,55,0.2)",
    border: "rgba(212,175,55,0.35)",
    change: `${todayApps.filter((a) => a.type === "Sesli").length} sesli, ${todayApps.filter((a) => a.type === "Yazılı").length} yazılı`,
  },
  {
    label: "Tamamlananlar",
    value: completed.length,
    icon: "ri-checkbox-circle-line",
    color: "#10b981",
    bg: "rgba(16,185,129,0.2)",
    border: "rgba(16,185,129,0.35)",
    change: "Bu ay",
  },
  {
    label: "Bekleyenler",
    value: pending.length,
    icon: "ri-time-line",
    color: "#f97316",
    bg: "rgba(249,115,22,0.2)",
    border: "rgba(249,115,22,0.35)",
    change: `${delayed.length} gecikmiş`,
  },
];

function TypeBadge({ type }: { type: string }) {
  const map: Record<string, { bg: string; color: string; icon: string }> = {
    Sesli: { bg: "rgba(168,85,247,0.25)", color: "#d8b4fe", icon: "ri-mic-line" },
    Yazılı: { bg: "rgba(249,115,22,0.25)", color: "#fdba74", icon: "ri-file-text-line" },
    Günlük: { bg: "rgba(212,175,55,0.25)", color: "#fde68a", icon: "ri-sun-line" },
  };
  const s = map[type] || map["Yazılı"];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: s.bg, color: s.color }}>
      <i className={`${s.icon} text-xs`} />
      {type}
    </span>
  );
}

function StatusDot({ status }: { status: string }) {
  const map: Record<string, string> = {
    Planlandı: "#c084fc",
    "Ödeme Bekliyor": "#fb923c",
    "Yeni Başvuru": "#fcd34d",
    Tamamlandı: "#34d399",
    Gecikti: "#f87171",
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: map[status] || "#fff" }}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: map[status] || "#fff" }} />
      {status}
    </span>
  );
}

export default function Dashboard() {
  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            ✦ Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>
            24 Mart 2026, Salı — Kozmik Yönetim Merkezi
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
          style={{ background: "rgba(212,175,55,0.18)", border: "1px solid rgba(212,175,55,0.4)", color: "#fde68a" }}>
          <i className="ri-moon-line" />
          <span>Ay: Boğa ♉</span>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl p-5 relative overflow-hidden transition-transform hover:-translate-y-0.5"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${card.border}`,
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: "rgba(255,255,255,0.65)" }}>
                  {card.label}
                </p>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {card.value}
                </p>
                <p className="text-xs mt-1.5 font-medium" style={{ color: card.color }}>
                  {card.change}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: card.bg }}>
                <i className={`${card.icon} text-lg`} style={{ color: card.color }} />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
              style={{ background: `linear-gradient(90deg, transparent, ${card.color}, transparent)` }} />
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-5 gap-5">
        {/* Today's Appointments */}
        <div className="col-span-3 rounded-2xl p-5"
          style={{
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              <i className="ri-sun-line text-sm" style={{ color: "#d4af37" }} />
              Bugünkü Danışmanlıklar
            </h2>
            <span className="text-xs px-2.5 py-1 rounded-full font-medium"
              style={{ background: "rgba(212,175,55,0.2)", color: "#fde68a", border: "1px solid rgba(212,175,55,0.35)" }}>
              {todayApps.length} randevu
            </span>
          </div>
          <div className="space-y-3">
            {todayApps.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: "rgba(255,255,255,0.5)" }}>
                Bugün randevu yok ✦
              </p>
            ) : (
              todayApps.map((app) => (
                <div key={app.id} className="flex items-center gap-4 px-4 py-3 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div className="w-2 h-8 rounded-full shrink-0" style={{ background: app.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{app.clientName}</p>
                    <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.6)" }}>
                      {app.service}
                    </p>
                  </div>
                  <TypeBadge type={app.type} />
                  {app.time && (
                    <span className="text-xs font-mono font-semibold" style={{ color: "rgba(255,255,255,0.75)" }}>
                      {app.time}
                    </span>
                  )}
                  <StatusDot status={app.status} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="col-span-2 space-y-4">
          {/* Tomorrow */}
          <div className="rounded-2xl p-5"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(168,85,247,0.3)",
            }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-white text-sm flex items-center gap-2"
                style={{ fontFamily: "'Playfair Display', serif" }}>
                <i className="ri-moon-clear-line text-sm" style={{ color: "#c084fc" }} />
                Yarın
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: "rgba(168,85,247,0.25)", color: "#d8b4fe" }}>
                {tomorrowApps.length}
              </span>
            </div>
            <div className="space-y-2">
              {tomorrowApps.slice(0, 3).map((app) => (
                <div key={app.id} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  style={{ background: "rgba(255,255,255,0.07)" }}>
                  <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: app.color }} />
                  <p className="text-xs font-medium text-white/85 truncate flex-1">{app.clientName}</p>
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>{app.service}</span>
                </div>
              ))}
              {tomorrowApps.length === 0 && (
                <p className="text-xs text-center py-2" style={{ color: "rgba(255,255,255,0.5)" }}>Yarın boş ✦</p>
              )}
            </div>
          </div>

          {/* Delayed */}
          <div className="rounded-2xl p-5"
            style={{
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.35)",
            }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm flex items-center gap-2" style={{ color: "#fca5a5" }}>
                <i className="ri-alarm-warning-line text-sm" style={{ color: "#f87171" }} />
                Geciken İşler
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: "rgba(239,68,68,0.3)", color: "#f87171" }}>
                {delayed.length}
              </span>
            </div>
            <div className="space-y-2">
              {delayed.length === 0 ? (
                <p className="text-xs text-center py-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                  Geciken iş yok ✦
                </p>
              ) : (
                delayed.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                    style={{ background: "rgba(239,68,68,0.12)" }}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ background: "rgba(239,68,68,0.3)", color: "#f87171" }}>
                      {c.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white/90 truncate">{c.name}</p>
                      <p className="text-xs truncate" style={{ color: "#f87171" }}>{c.service}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Bu Haftaki Toplam", value: "8 danışmanlık", icon: "ri-bar-chart-line", color: "#a855f7" },
          { label: "Ortalama Teslim Süresi", value: "4.2 gün", icon: "ri-timer-line", color: "#d4af37" },
          { label: "Bu Ayki Toplam İş", value: `${mockClients.length} danışmanlık`, icon: "ri-star-line", color: "#10b981" },
        ].map((item) => (
          <div key={item.label} className="rounded-xl px-5 py-4 flex items-center gap-4"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)" }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: `${item.color}28` }}>
              <i className={`${item.icon} text-base`} style={{ color: item.color }} />
            </div>
            <div>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{item.label}</p>
              <p className="text-sm font-semibold text-white mt-0.5">{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
